import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../../components/Portal/PortalHeader';
import { setLessonOutcome, outcomeChoices, OUTCOME_META } from '../../../services/lessonOutcome';
import { ymd } from '../../../utils/occurrenceExpansion';
import {
	TEACHER_SOURCE_LABEL,
	TAUGHT_SOURCE_LABEL,
	taughtTeacher,
	hasTeacher,
	substitutionKey,
	monthRange,
	shiftMonth,
	monthOf,
	monthLabel,
	summarizeMonth,
	outcomeText,
} from './lessonsTaughtModel';
import './LessonsTaught.css';

/**
 * Lessons taught — admin-only, per teacher per month (card
 * dQzkKYcUNzuvhO0DEDES, 2026-09-23). The count Annelise is paid from: ATTENDED
 * lessons carrying her teacherId. Everything that could make that number wrong
 * is on the same page and fixable in place — an unmarked past lesson (mark it)
 * and a lesson with no teacher (set one). Setting a teacher here writes
 * teacherSource 'admin', which the nightly stamp never overwrites.
 *
 * 2026-10-03 (evening): an attended lesson is credited to the SCHEDULED
 * teacher, whoever's login marks it; an admin changes "Taught by" here when
 * someone else covered (taughtBySource 'admin', final). "Needs your OK" holds
 * only an attended lesson with no scheduled teacher, plus any record marked
 * under that morning's marker rule that the re-credit has not reached. See
 * functions/lessonTaughtBy.js.
 */

const fmtDate = (s) => {
	const [y, m, d] = String(s || '').split('-').map(Number);
	if (!y) return s || '';
	return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtTime = (hhmm) => {
	const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
	if (!m) return '';
	const h = Number(m[1]);
	return `${((h + 11) % 12) + 1}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};

const nameOf = (teachers, id) => (teachers.find((t) => t.id === id) || {}).name || 'Unknown teacher';

/** Who taught it: a picker for an attended lesson, a dash for anything else. */
function TaughtByCell({ r, teachers, busy, onTaughtBy }) {
	if (r.outcome !== 'attended') return <span className='lt-sub'>Not taught</span>;
	const t = taughtTeacher(r);
	const note = t.via === 'schedule' ? 'From the schedule (before tracking)'
		: TAUGHT_SOURCE_LABEL[r.taughtBySource] || 'Not recorded';
	return (
		<>
			<select
				className='ps-select lt-select'
				aria-label={`Taught by, ${r.studentName || 'lesson'} on ${fmtDate(r.date)}`}
				value={t.teacherId || ''}
				disabled={busy}
				onChange={(e) => e.target.value && onTaughtBy(r, e.target.value)}>
				<option value=''>Not recorded</option>
				{teachers.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
			</select>
			<div className='lt-sub'>{note}</div>
		</>
	);
}

/**
 * Substitutions waiting on an OK. One tap confirms the teacher who marked it;
 * the picker sets someone else (which is itself the decision, so it leaves
 * the list too).
 */
function ReviewList({ lessons, teachers, pickable, busyId, onConfirm, onTaughtBy }) {
	return (
		<section className='lt-review' aria-label='Substitutions to confirm'>
			<h3 className='lt-detail__title'>
				<i className='fa-sharp fa-solid fa-user-check' aria-hidden='true' /> Needs your OK
			</h3>
			<p className='lt-sub lt-review__intro'>
				These attended lessons have no scheduled teacher to credit. Pick who taught each one
				before paying from this month.
			</p>
			<div className='ps-table-wrap'>
				<table className='ps-table lt-table'>
					<thead>
						<tr>
							<th>Date</th>
							<th>Student</th>
							<th>Scheduled</th>
							<th>Taught by</th>
							<th><span className='visually-hidden'>Confirm</span></th>
						</tr>
					</thead>
					<tbody>
						{lessons.map((r) => {
							const busy = busyId === r.id;
							return (
								<tr key={r.id}>
									<td data-label='Date' className='lt-cell--date'>
										{fmtDate(r.date)}
										{r.time ? <div className='lt-sub lt-nowrap'>{fmtTime(r.time)}{r.location ? `\u00a0·\u00a0${r.location}` : ''}</div> : null}
									</td>
									<td data-label='Student'>{r.studentName || 'Unnamed'}</td>
									<td data-label='Scheduled'>{r.teacherId ? nameOf(teachers, r.teacherId) : 'No one'}</td>
									<td data-label='Taught by'>
										<TaughtByCell r={r} teachers={pickable} busy={busy} onTaughtBy={onTaughtBy} />
									</td>
									<td className='lt-cell--action'>
										{r.taughtBy ? (
											<button type='button' className='ps-btn lt-confirm' disabled={busy} onClick={() => onConfirm(r)}>
												Confirm {nameOf(teachers, r.taughtBy)}
											</button>
										) : (
											<span className='lt-sub'>Pick who taught it</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function LessonTable({ lessons, teachers, today, busyId, onTeacher, onTaughtBy, onOutcome }) {
	if (!lessons.length) return <div className='ps-empty'>No lessons this month.</div>;
	return (
		<div className='ps-table-wrap'>
			<table className='ps-table lt-table'>
				<thead>
					<tr>
						<th>Date</th>
						<th>Student</th>
						<th>Outcome</th>
						<th>Scheduled</th>
						<th>Taught by</th>
					</tr>
				</thead>
				<tbody>
					{lessons.map((r) => {
						const text = outcomeText(r, today);
						const busy = busyId === r.id;
						return (
							<tr key={r.id}>
								<td data-label='Date' className='lt-cell--date'>
									{fmtDate(r.date)}
									{r.time ? <div className='lt-sub lt-nowrap'>{fmtTime(r.time)}{r.location ? `\u00a0·\u00a0${r.location}` : ''}</div> : null}
								</td>
								<td data-label='Student'>
									{r.studentName || 'Unnamed'}
									{r.lessonType === 'Make-up' ? <div className='lt-sub'>Makeup</div> : null}
								</td>
								<td data-label='Outcome'>
									{text === 'Upcoming' ? (
										<span className='lt-sub'>Upcoming</span>
									) : (
										<select
											className={`ps-select lt-select${text === 'Unmarked' ? ' lt-select--unmarked' : ''}`}
											aria-label={`Outcome for ${r.studentName || 'lesson'} on ${fmtDate(r.date)}`}
											value={r.outcome || ''}
											disabled={busy}
											onChange={(e) => onOutcome(r, e.target.value || null)}>
											<option value=''>Unmarked</option>
											{outcomeChoices({ isAdmin: true }).map((v) => (
												<option key={v} value={v}>{OUTCOME_META[v].label}</option>
											))}
										</select>
									)}
								</td>
								<td data-label='Scheduled'>
									<select
										className='ps-select lt-select'
										aria-label={`Scheduled teacher for ${r.studentName || 'lesson'} on ${fmtDate(r.date)}`}
										value={r.teacherSource && r.teacherSource !== 'unresolved' ? r.teacherId || '' : ''}
										disabled={busy}
										onChange={(e) => e.target.value && onTeacher(r, e.target.value)}>
										<option value=''>No teacher</option>
										{teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
									</select>
									<div className='lt-sub'>{TEACHER_SOURCE_LABEL[r.teacherSource] || 'Not stamped yet'}</div>
								</td>
								<td data-label='Taught by'>
									<TaughtByCell r={r} teachers={teachers} busy={busy} onTaughtBy={onTaughtBy} />
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export default function LessonsTaught() {
	const { currentUser } = useAuth();
	const [month, setMonth] = useState(() => monthOf(new Date()));
	const [records, setRecords] = useState([]);
	const [teachers, setTeachers] = useState([]);
	const [status, setStatus] = useState('loading'); // loading | ready | error
	const [error, setError] = useState(null);
	const [saveError, setSaveError] = useState(null);
	const [busyId, setBusyId] = useState(null);
	const [open, setOpen] = useState(null); // teacherId (or 'none') whose lessons are shown

	const today = ymd(new Date());

	const load = useCallback(async () => {
		const range = monthRange(month);
		setStatus('loading');
		setError(null);
		try {
			const [recSnap, teacherSnap] = await Promise.all([
				getDocs(query(collection(db, 'lessons'), where('date', '>=', range.from), where('date', '<=', range.to))),
				getDocs(collection(db, 'teachers')),
			]);
			setRecords(recSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
			// ALL teachers, inactive included: a teacher who left mid-month still
			// has lessons to be counted and named. Only the pickers filter.
			setTeachers(teacherSnap.docs
				.map((d) => ({ id: d.id, name: d.data().name || 'Teacher', active: d.data().active !== false }))
				.sort((a, b) => a.name.localeCompare(b.name)));
			setStatus('ready');
		} catch (e) {
			console.error('Lessons taught: load failed', e);
			setError(e.message || 'Could not load lessons.');
			setStatus('error');
		}
	}, [month]);

	useEffect(() => { load(); }, [load]);

	// The roster for this month: every active teacher (so a zero is visible)
	// plus any inactive one who taught in it.
	const summary = useMemo(() => {
		const taught = new Set(records.map((r) => r.teacherId).filter(Boolean));
		return summarizeMonth(records, teachers.filter((t) => t.active || taught.has(t.id)), today);
	}, [records, teachers, today]);
	const activeTeachers = useMemo(() => teachers.filter((t) => t.active), [teachers]);

	const patchLocal = (id, patch) => setRecords((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

	const onTeacher = async (r, teacherId) => {
		if (busyId) return;
		setBusyId(r.id);
		setSaveError(null);
		try {
			await updateDoc(doc(db, 'lessons', r.id), {
				teacherId,
				teacherSource: 'admin',
				teacherSetBy: (currentUser && currentUser.uid) || null,
				teacherSetAt: serverTimestamp(),
			});
			patchLocal(r.id, { teacherId, teacherSource: 'admin' });
		} catch (e) {
			console.error('Lessons taught: teacher save failed', e);
			setSaveError(`Could not change the teacher for ${r.studentName || 'that lesson'}: ${e.message}`);
		} finally {
			setBusyId(null);
		}
	};

	// Who TAUGHT it, set by an admin: final (the trigger never overwrites
	// taughtBySource 'admin'), and itself the decision, so it leaves review.
	const onTaughtBy = async (r, teacherId) => {
		if (busyId) return;
		setBusyId(r.id);
		setSaveError(null);
		try {
			await updateDoc(doc(db, 'lessons', r.id), {
				taughtBy: teacherId,
				taughtBySource: 'admin',
				taughtBySetBy: (currentUser && currentUser.uid) || null,
				taughtByAt: serverTimestamp(),
			});
			patchLocal(r.id, { taughtBy: teacherId, taughtBySource: 'admin' });
		} catch (e) {
			console.error('Lessons taught: taught-by save failed', e);
			setSaveError(`Could not change who taught ${r.studentName || 'that lesson'}: ${e.message}`);
		} finally {
			setBusyId(null);
		}
	};

	// Confirm the teacher who marked it. Records the PAIR confirmed, so a later
	// change to either teacher puts the lesson back in review.
	const onConfirm = async (r) => {
		if (busyId) return;
		const key = substitutionKey(r);
		if (!key) return;
		setBusyId(r.id);
		setSaveError(null);
		try {
			await updateDoc(doc(db, 'lessons', r.id), {
				substitutionConfirmedFor: key,
				substitutionConfirmedBy: (currentUser && currentUser.uid) || null,
				substitutionConfirmedAt: serverTimestamp(),
			});
			patchLocal(r.id, { substitutionConfirmedFor: key });
		} catch (e) {
			console.error('Lessons taught: confirm failed', e);
			setSaveError(`Could not confirm ${r.studentName || 'that lesson'}: ${e.message}`);
		} finally {
			setBusyId(null);
		}
	};

	const onOutcome = async (r, value) => {
		if (busyId) return;
		setBusyId(r.id);
		setSaveError(null);
		try {
			// Marking attended HERE is bookkeeping, not teaching: without this the
			// taught-by trigger would credit the admin who clicked and flag a
			// substitution. So the scheduled teacher is recorded as the one who
			// taught it, as an admin decision, in the same write. The Taught by
			// picker changes it if someone else did.
			const uid = (currentUser && currentUser.uid) || null;
			const extra = value === 'attended' && hasTeacher(r) && r.taughtBySource !== 'admin'
				? { taughtBy: r.teacherId, taughtBySource: 'admin', taughtBySetBy: uid, taughtByAt: serverTimestamp() }
				: null;
			await setLessonOutcome({ row: { lessonDocId: r.id }, value, uid, extra });
			patchLocal(r.id, { outcome: value || undefined, ...(extra ? { taughtBy: extra.taughtBy, taughtBySource: 'admin' } : {}) });
		} catch (e) {
			console.error('Lessons taught: outcome save failed', e);
			setSaveError(`Could not mark ${r.studentName || 'that lesson'}: ${e.message}`);
		} finally {
			setBusyId(null);
		}
	};

	const groups = summary.unattributed.lessons.length
		? [...summary.rows, { ...summary.unattributed, teacherId: 'none' }]
		: summary.rows;
	const shown = groups.find((g) => g.teacherId === open);

	return (
		<>
			<PortalHeader
				title='Lessons Taught'
				subtitle='Lessons each teacher taught, per month, credited to the scheduled teacher. Change Taught by when someone else covered. This is the count teacher pay is based on.'
			/>
			<div className='ps-page lessons-taught'>
				<div className='lt-monthbar'>
					<button type='button' className='ps-btn lt-navbtn' onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label='Previous month'>
						<i className='fa-sharp fa-solid fa-chevron-left' aria-hidden='true' />
					</button>
					<h2 className='ps-h2 lt-month'>{monthLabel(month)}</h2>
					<button type='button' className='ps-btn lt-navbtn' onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label='Next month'>
						<i className='fa-sharp fa-solid fa-chevron-right' aria-hidden='true' />
					</button>
				</div>

				{status === 'loading' && <div className='ps-spinner' role='status' aria-label='Loading' />}
				{status === 'error' && <p className='ps-alert ps-alert--danger'>{error}</p>}
				{saveError && <p className='ps-alert ps-alert--danger'>{saveError}</p>}

				{status === 'ready' && (
					<>
						{summary.review.length > 0 && (
							<ReviewList
								lessons={summary.review}
								teachers={teachers}
								pickable={activeTeachers}
								busyId={busyId}
								onConfirm={onConfirm}
								onTaughtBy={onTaughtBy}
							/>
						)}

						{(summary.totals.unmarked > 0 || summary.totals.unattributed > 0) && (
							<p className='ps-alert ps-alert--warning'>
								{summary.totals.unmarked > 0 && (
									<>{summary.totals.unmarked} past lesson{summary.totals.unmarked === 1 ? ' is' : 's are'} not marked yet. Only attended lessons count, so mark them before paying. </>
								)}
								{summary.totals.unattributed > 0 && (
									<>{summary.totals.unattributed} lesson{summary.totals.unattributed === 1 ? ' has' : 's have'} no teacher and {summary.totals.unattributed === 1 ? 'is' : 'are'} in nobody&rsquo;s count.</>
								)}
							</p>
						)}

						<div className='ps-table-wrap'>
							<table className='ps-table lt-summary'>
								<thead>
									<tr>
										<th>Teacher</th>
										<th className='lt-num'>Taught</th>
										<th className='lt-num'>Covering</th>
										<th className='lt-num'>Unmarked</th>
										<th className='lt-num'>No-show</th>
										<th className='lt-num'>Cancelled</th>
										<th className='lt-num'>Upcoming</th>
										<th><span className='visually-hidden'>Lessons</span></th>
									</tr>
								</thead>
								<tbody>
									{groups.map((g) => (
										<tr key={g.teacherId} className={g.teacherId === 'none' ? 'lt-row--none' : undefined}>
											<td className='lt-name'>{g.name}</td>
											<td data-label='Taught' className='lt-num lt-paid'>{g.attended}</td>
											<td data-label='Covering' className='lt-num' title='Taught, but scheduled for another teacher'>{g.covered}</td>
											<td data-label='Unmarked' className={`lt-num${g.unmarked ? ' lt-warn' : ''}`}>{g.unmarked}</td>
											<td data-label='No-show' className='lt-num'>{g.noShow}</td>
											<td data-label='Cancelled' className='lt-num'>{g.cancelled}</td>
											<td data-label='Upcoming' className='lt-num'>{g.upcoming}</td>
											<td className='lt-cell--action'>
												<button
													type='button'
													className='ps-btn lt-show'
													aria-expanded={open === g.teacherId}
													onClick={() => setOpen((o) => (o === g.teacherId ? null : g.teacherId))}>
													{open === g.teacherId ? 'Hide lessons' : `Show ${g.lessons.length}`}
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{shown && (
							<section className='lt-detail' aria-label={`${shown.name} lessons`}>
								<h3 className='lt-detail__title'>{shown.name} &middot; {monthLabel(month)}</h3>
								<LessonTable
									lessons={shown.lessons}
									teachers={activeTeachers}
									today={today}
									busyId={busyId}
									onTeacher={onTeacher}
									onTaughtBy={onTaughtBy}
									onOutcome={onOutcome}
								/>
							</section>
						)}
					</>
				)}
			</div>
		</>
	);
}
